"use client";
import { SuccessIcon } from "@shared/ui.icons";
import Markdown from "markdown-to-jsx";
import Image from "next/image";

import { Button } from "../Button/Button";
import { Link } from "../Link/Link";
import { ProgressBar, type ProgressBarProps } from "../ProgressBar/ProgressBar";
import { Typography } from "../Typography/Typography";

import * as styles from "./PromoTile.css";

export type PromoTileVariant = "standard" | "slim";
export type PromoTileLayout = "padding" | "full";
export type LinkAlignment = "start" | "end";

type PromoTileImage = {
  src: string;
  altText: string;
};

interface PromoTileProps {
  title: string;
  description: string;
  buttonText?: string;
  onClick?: () => void;
  href?: string;
  image?: PromoTileImage;
  progress?: ProgressBarProps | undefined;
  successIcon?: boolean;
  linkAsButton?: boolean;
  /**
   * The size variant of the PromoTile.
   * 'standard': 104px height
   * 'slim': 60px height
   * @default 'standard'
   * @type {'standard' | 'slim'}
   */
  variant?: PromoTileVariant;
  /**
   * The layout style of the PromoTile.
   * 'padding': Adds padding around the content
   * 'full': Full bleed image with no padding
   * @default 'padding'
   *  @type { 'padding' | 'full' }
   */
  layout?: PromoTileLayout;
  /**
   * The alignment of the link/button.
   * 'start': Aligns to the start of the container
   * 'end': Aligns to the end of the container
   * @default 'end'
   * @type { 'start' | 'end' }
   */
  linkAlign?: LinkAlignment;
}

const imageSizeOptions = {
  slim: {
    padding: 56,
    full: 80
  },
  standard: {
    padding: 80,
    full: 104
  }
};

export const PromoTile = ({
  title,
  description,
  buttonText,
  onClick,
  href,
  image,
  progress,
  successIcon = false,
  linkAsButton = true,
  variant = "standard",
  layout = "padding",
  linkAlign = "end"
}: PromoTileProps) => {
  const hasFooterContent = progress || buttonText;

  return (
    <div className={styles.promoTileContainer({ variant, layout })}>
      {image && (
        <div className={styles.imageContainer({ variant, layout })}>
          <Image
            src={image?.src}
            alt={image?.altText || title}
            width={imageSizeOptions[variant][layout]}
            height={imageSizeOptions[variant][layout]}
            priority
            sizes="300px"
            objectFit="contain"
          />
        </div>
      )}

      <div className={styles.contentContainer({ variant, layout })}>
        <div className={styles.contentTitle}>
          <Typography variant="subheader-7">{title}</Typography>{" "}
          {successIcon && (
            <SuccessIcon
              data-testid="success-icon"
              className={styles.successIcon}
            />
          )}
        </div>

        <div className={styles.contentBody}>
          <Typography variant="smallprint-md" color="primary-medium">
            <Markdown>{description}</Markdown>
          </Typography>
        </div>

        {hasFooterContent && (
          <div className={styles.contentFooter}>
            {(progress && (
              <ProgressBar
                current={progress.current}
                threshold={progress.threshold}
              />
            )) ||
              (buttonText && (
                <div className={styles.linkContainer({ linkAlign })}>
                  {linkAsButton ? (
                    <Button
                      variant="link"
                      size="small"
                      noPadding
                      onClick={onClick}
                      text={buttonText}
                    />
                  ) : (
                    <Link href={href || "#"} text={buttonText} size="small" />
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
